/**
 * Dynamic API Configuration
 * Automatically detects the correct API URL based on current context
 */

// Type declaration for runtime environment
declare global {
  interface Window {
    __ENV__?: {
      VITE_API_BASE_URL?: string;
      VITE_API_URL?: string;
      VITE_WS_URL?: string;
      VITE_APP_NAME?: string;
      VITE_GA_MEASUREMENT_ID?: string;
    };
  }
}

/**
 * Get environment variable from runtime config (window.__ENV__) or build-time (import.meta.env)
 * Runtime config takes precedence if available and non-empty
 */
const getEnv = (key: string): string | undefined => {
  if (typeof window !== "undefined") {
    const runtimeValue = window.__ENV__?.[key as keyof typeof window.__ENV__];
    if (runtimeValue) {
      return runtimeValue;
    }
  }
  return import.meta.env[key];
};

// Get the current hostname from the browser
const getCurrentHostname = (): string => {
  if (typeof window !== "undefined") {
    const hostname = window.location.hostname;
    // If hostname is localhost but we're actually accessing via IP,
    // try to detect the real IP from the URL
    if (hostname === "localhost" && window.location.href.includes("://")) {
      const urlMatch = window.location.href.match(/https?:\/\/([^:\/]+)/);
      if (urlMatch && urlMatch[1] !== "localhost") {
        return urlMatch[1];
      }
    }
    return hostname;
  }
  return "localhost";
};

// Get the current protocol (http or https)
const getCurrentProtocol = (): string => {
  if (typeof window !== "undefined") {
    return window.location.protocol === "https:" ? "https:" : "http:";
  }
  return "http:";
};

// Determine if we're running in localhost or network access mode
const isLocalhost = (): boolean => {
  const hostname = getCurrentHostname();
  return hostname === "localhost" || hostname === "127.0.0.1";
};

// Get WebSocket protocol
const getWsProtocol = (): string => {
  return getCurrentProtocol() === "https:" ? "wss:" : "ws:";
};

// Dynamic API configuration
export const getApiConfig = () => {
  const hostname = getCurrentHostname();
  const protocol = getCurrentProtocol();
  const wsProtocol = getWsProtocol();

  // Use HTTPS for API when frontend is HTTPS to avoid mixed content issues
  const apiProtocol = protocol === "https:" ? "https:" : "http:";

  // Use runtime env (window.__ENV__) or build-time env, otherwise use dynamic detection
  const baseUrl = getEnv("VITE_API_BASE_URL") || `${apiProtocol}//${hostname}`;
  const apiUrl = getEnv("VITE_API_URL") || `${baseUrl}/api/v1`;
  const wsUrl = getEnv("VITE_WS_URL") || `${wsProtocol}//${hostname}`;

  return {
    API_BASE_URL: baseUrl,
    API_URL: apiUrl,
    WS_URL: wsUrl,
    APP_NAME: getEnv("VITE_APP_NAME") || 'Ankassa',
    IS_LOCALHOST: isLocalhost(),
    CURRENT_HOSTNAME: hostname,
    CURRENT_PROTOCOL: protocol,
  };
};

// ... existing exports ...

// Helper to get API URL with fallback - can be used in import.meta.env fallbacks
export const getApiUrlFallback = (): string => {
  if (typeof window !== "undefined") {
    return getApiConfig().API_URL;
  }
  return "http://localhost:4000/api/v1"; // SSR fallback
};

// Helper function to get the full API URL for a specific endpoint
export const getApiUrl = (endpoint: string): string => {
  const config = getApiConfig();
  return `${config.API_URL}${endpoint.startsWith("/") ? endpoint : `/${endpoint}`}`;
};

// Helper function to get the WebSocket URL
export const getWebSocketUrl = (): string => {
  const config = getApiConfig();
  return config.WS_URL;
};

// Debug info (only in development)
if (import.meta.env.DEV) {
  const config = getApiConfig();
  console.log("🌐 Dynamic API Configuration:", config);
  console.log("🔍 Hostname detected:", config.CURRENT_HOSTNAME);
  console.log("🔗 API URL being used:", config.API_URL);
  console.log(
    "🌍 Current window location:",
    typeof window !== "undefined" ? window.location.href : "SSR"
  );
  console.log("📡 Protocol detected:", config.CURRENT_PROTOCOL);
}
